import React, { useState } from 'react';
import { Platform, StyleSheet, Text, View } from 'react-native';
import Svg, { Circle, G, Path } from 'react-native-svg';

import { buildPieShapes } from './pie-chart-geometry';

export interface PieSlice {
	label: string;
	/** Sizing weight — slice's share of the donut. */
	value: number;
	color: string;
	/** Optional secondary info shown in the tooltip (count, $ amount, etc.). */
	tooltip?: string;
	/** Click / tap handler — wire up navigation to filtered list pages. */
	onPress?: () => void;
}

interface Props {
	slices: PieSlice[];
	size?: number;
	/** Default text shown in the center when nothing is hovered. */
	centerLabel?: string;
	centerSub?: string;
}

/**
 * Donut chart with hover tooltip + clickable slices.
 *
 *   - Web: hovering a slice replaces centerLabel/Sub with that slice's
 *     details (label + tooltip). Click invokes the slice's onPress.
 *   - Native: tap invokes onPress directly (no hover state to maintain).
 *
 * Renders each slice as a sector Path; cuts a hole with a white Circle
 * so the result reads as a donut. No legend — the tooltip replaces it.
 */
export const PieChart: React.FC<Props> = ({ slices, size = 180, centerLabel, centerSub }) => {
	const cx = size / 2;
	const cy = size / 2;
	const r = size / 2 - 2;
	const rInner = r * 0.6;

	const [hovered, setHovered] = useState<number | null>(null);

	const { total, shapes } = buildPieShapes(slices.map((s) => s.value), { cx, cy, r });

	if (!(total > 0)) {
		return (
			<View style={[styles.wrap, { width: size, height: size }]}>
				<View style={[styles.emptyCircle, { width: size, height: size, borderRadius: size / 2 }]}>
					<Text style={styles.emptyLabel}>No data</Text>
				</View>
			</View>
		);
	}

	const paths = shapes.map((shape) => {
		const i = shape.index;
		const slice = slices[i];
		const isHovered = hovered === i;
		// On web, drive interaction with raw DOM handlers (onClick + hover) rather
		// than RN's `onPress`. react-native-svg routes `onPress` through
		// react-native-web's responder system, which leaks `onStartShouldSetResponder`
		// onto the DOM <path>/<circle> — react-dom logs "Unknown event handler
		// property" for every slice. `onPress` is the right prop only on native.
		const interaction = Platform.OS === 'web' ? {
			onClick: slice.onPress,
			onMouseEnter: () => setHovered(i),
			onMouseLeave: () => setHovered((h) => (h === i ? null : h)),
			...(slice.onPress ? { style: { cursor: 'pointer' } } : {}),
		} : {
			onPress: slice.onPress,
		};
		const common = {
			fill: slice.color,
			opacity: hovered === null || isHovered ? 1 : 0.55,
			...interaction,
		};
		return shape.kind === 'circle'
			? <Circle key={i} cx={cx} cy={cy} r={r} {...common} />
			: <Path key={i} d={shape.d} {...common} />;
	});

	const hoveredSlice = hovered != null ? slices[hovered] : null;
	const showLabel = hoveredSlice?.label ?? centerLabel;
	const showSub = hoveredSlice?.tooltip ?? centerSub;

	return (
		<View style={[styles.wrap, { width: size, height: size }]}>
			<Svg width={size} height={size}>
				<G>{paths}</G>
				<Circle cx={cx} cy={cy} r={rInner} fill="#fff" />
			</Svg>
			{(showLabel || showSub) && (
				<View style={[styles.centerOverlay, { width: size, height: size }]} pointerEvents="none">
					<Text style={styles.centerLabel} numberOfLines={1}>{showLabel}</Text>
					{showSub && <Text style={styles.centerSub} numberOfLines={2}>{showSub}</Text>}
				</View>
			)}
		</View>
	);
};

const styles = StyleSheet.create({
	wrap: { alignItems: 'center', justifyContent: 'center' },
	centerOverlay: {
		position: 'absolute', alignItems: 'center', justifyContent: 'center',
		paddingHorizontal: 18,
	},
	centerLabel: { fontSize: 16, fontWeight: '700', color: '#212121', textAlign: 'center' },
	centerSub: { fontSize: 11, color: '#546e7a', marginTop: 2, textAlign: 'center' },
	emptyCircle: { borderWidth: 1, borderColor: '#e0e7ef', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f5f7fa' },
	emptyLabel: { color: '#90a4ae', fontSize: 13 },
});
